"use client";

/**
 * DemoBar — the floating bottom navbar that drives the click-through.
 *
 * Layout (left → right):
 *   [DEMO chip] [role chip] · [narrator (truncates)] · [step n/N] · [Prev][Next] · [Restart] · [Exit]
 *
 * Sticky to the viewport bottom, full-width on mobile, max-w-5xl centered on
 * desktop. Auto-truncates the narrator on small screens; the "sub" line is
 * hidden on mobile to keep the bar one row.
 */

import { useRef, useState } from 'react';
import {
  ArrowLeft, ArrowRight, RotateCcw, X, Loader2, PlayCircle, Sparkles,
} from 'lucide-react';
import { useDemo } from './DemoProvider';

/**
 * Tiny scoped click-feedback wrapper around <button>. Two effects:
 *   1. The button scales down ~4% while pressed (`active:scale-[0.96]`)
 *   2. A coral ripple expands from the click point, fades out, then unmounts
 *
 * We do this inline (not in a global CSS file) so the demo bar stays a
 * single-file drop-in and doesn't risk a Tailwind purge dropping the
 * animation classes. Pure DOM + setTimeout — no extra deps.
 */
type RippleButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  rippleColor?: string;
};

function RippleButton({ children, onClick, rippleColor = 'rgba(255,255,255,0.55)', className, style, ...rest }: RippleButtonProps) {
  const [ripples, setRipples] = useState<{ id: number; x: number; y: number; size: number }[]>([]);
  const counterRef = useRef(0);

  return (
    <button
      {...rest}
      onClick={(e) => {
        if (rest.disabled) return;
        const target = e.currentTarget;
        const rect = target.getBoundingClientRect();
        // Ripple should grow to cover the longest distance from the click
        // point to any corner of the button — guarantees it fully fills.
        const size = Math.max(rect.width, rect.height) * 2;
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;
        const id = ++counterRef.current;
        setRipples(prev => [...prev, { id, x, y, size }]);
        // Auto-clean after the animation has fully played out.
        window.setTimeout(() => {
          setRipples(prev => prev.filter(r => r.id !== id));
        }, 650);
        onClick?.(e);
      }}
      className={`relative overflow-hidden transition-transform active:scale-[0.96] ${className ?? ''}`}
      style={style}
    >
      {/* Ripple layer — absolutely positioned circles that scale + fade out. */}
      {ripples.map(r => (
        <span
          key={r.id}
          aria-hidden
          className="pointer-events-none absolute rounded-full demo-ripple-anim"
          style={{
            left: r.x,
            top: r.y,
            width: r.size,
            height: r.size,
            background: rippleColor,
          }}
        />
      ))}
      {/* Keyframes — scoped via a single global rule, idempotent injection. */}
      <RippleKeyframes />
      {children}
    </button>
  );
}

let keyframesInjected = false;
function RippleKeyframes() {
  if (typeof document !== 'undefined' && !keyframesInjected) {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes demo-ripple {
        0%   { transform: scale(0); opacity: 0.55; }
        100% { transform: scale(1); opacity: 0;    }
      }
      .demo-ripple-anim {
        animation: demo-ripple 600ms ease-out forwards;
      }
    `;
    document.head.appendChild(style);
    keyframesInjected = true;
  }
  return null;
}

const C = {
  ink: '#2B2240', inkSoft: '#5B5170', inkMute: '#8A8298',
  hair: 'rgba(43,34,64,0.10)',
  coral: '#FF6B6B', coralDeep: '#E8484C', tangerine: '#FF9F43',
  mint: '#21C7A8', mintDeep: '#0E8E76',
  indigoDeep: '#3F3FC2',
};

export default function DemoBar() {
  const {
    step, stepIndex, totalSteps, goNext, goPrev, exit, start, navigating, roleLabel,
  } = useDemo();
  if (!step) return null;

  const atStart = stepIndex <= 0;
  const atEnd = stepIndex >= totalSteps - 1;

  return (
    <>
      {/* Spacer so page content scrolls past the fixed bar.
          Height matches the bar itself; mobile has a taller bar so we account
          for both. Conservative: 96px (1 line) → 120px (2 lines). */}
      <div aria-hidden style={{ height: 132 }} />

      <div
        className="fixed bottom-0 left-0 right-0 z-[100] pointer-events-none"
        role="region"
        aria-label="Demo controls"
      >
        <div className="max-w-5xl mx-auto px-3 sm:px-4 pb-3 sm:pb-4">
          <div
            className="pointer-events-auto rounded-2xl shadow-2xl overflow-hidden"
            style={{
              background: 'rgba(255, 248, 239, 0.96)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              border: `1px solid ${C.hair}`,
              boxShadow: '0 18px 50px -20px rgba(43,34,64,0.40), 0 6px 18px -8px rgba(43,34,64,0.20)',
            }}
          >
            {/* Top row — narrator + step counter */}
            <div className="flex items-center gap-3 px-4 sm:px-5 pt-3">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider flex-shrink-0"
                style={{ background: C.coralDeep, color: '#fff' }}
              >
                <Sparkles className="w-3 h-3" /> Demo
              </span>
              <span
                className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider flex-shrink-0"
                style={{ background: 'rgba(91,91,214,0.10)', color: C.indigoDeep }}
              >
                {roleLabel}
              </span>
              <span className="flex-1 text-[12.5px] font-bold truncate" style={{ color: C.ink }}>
                {step.narrator}
              </span>
              <span className="text-[11px] font-extrabold flex-shrink-0" style={{ color: C.inkMute }}>
                {stepIndex + 1} / {totalSteps}
              </span>
            </div>

            {/* Sub line — only on sm+, gives room for the second hint */}
            {step.sub && (
              <div className="hidden sm:block px-5 pb-1 text-[11.5px]" style={{ color: C.inkSoft }}>
                {step.sub}
              </div>
            )}

            {/* Mobile role chip on its own row */}
            <div className="sm:hidden px-4 pt-1.5">
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                style={{ background: 'rgba(91,91,214,0.10)', color: C.indigoDeep }}
              >
                {roleLabel}
              </span>
            </div>

            {/* Bottom row — controls */}
            <div className="flex items-center gap-2 px-3 sm:px-4 py-3">
              <RippleButton
                type="button"
                onClick={() => goPrev()}
                disabled={atStart || navigating}
                rippleColor="rgba(232,72,76,0.18)"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12.5px] font-bold transition disabled:opacity-40"
                style={{ background: '#fff', color: C.ink, border: `1px solid ${C.hair}` }}
                aria-label="Previous step"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Back</span>
              </RippleButton>

              <RippleButton
                type="button"
                onClick={() => (atEnd ? start() : goNext())}
                disabled={navigating}
                rippleColor="rgba(255,255,255,0.55)"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-extrabold text-white transition disabled:opacity-60 flex-1"
                style={{ background: `linear-gradient(135deg, ${C.coral}, ${C.coralDeep})` }}
                aria-label={atEnd ? 'Restart demo' : 'Next step'}
              >
                {navigating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : atEnd ? (
                  <><PlayCircle className="w-3.5 h-3.5" /> Restart from the top</>
                ) : (
                  <>Next <ArrowRight className="w-3.5 h-3.5" /></>
                )}
              </RippleButton>

              <RippleButton
                type="button"
                onClick={() => start()}
                disabled={navigating}
                rippleColor="rgba(232,72,76,0.16)"
                className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11.5px] font-semibold transition disabled:opacity-40"
                style={{ color: C.inkSoft, background: 'transparent', border: `1px solid ${C.hair}` }}
                aria-label="Restart from beginning"
                title="Restart demo from the start"
              >
                <RotateCcw className="w-3 h-3" /> Restart
              </RippleButton>

              <RippleButton
                type="button"
                onClick={() => exit()}
                disabled={navigating}
                rippleColor="rgba(232,72,76,0.22)"
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11.5px] font-semibold transition disabled:opacity-40"
                style={{ color: C.coralDeep, background: 'transparent', border: `1px solid rgba(232,72,76,0.20)` }}
                aria-label="Exit demo mode"
              >
                <X className="w-3 h-3" /> Exit
              </RippleButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
